import React from "react";
import type { FormikProps } from "formik";

import LabeledTextField, { type LabeledTextFieldProps } from "./LabeledTextField";

function getFieldErrorProps(formik: FormikProps<any>, name: string) {
  const touched = Boolean((formik.touched as any)?.[name]);
  const rawError = (formik.errors as any)?.[name];
  const errorMessage = typeof rawError === "string" ? rawError : undefined;

  return {
    error: Boolean(touched && errorMessage),
    helperText: touched ? errorMessage : undefined,
  };
}

type CommonProps = Omit<
  LabeledTextFieldProps,
  "id" | "value" | "onChange" | "onBlur" | "error" | "helperText" | "type"
>;

export type FormikLabeledTextFieldProps = CommonProps & {
  formik: FormikProps<any>;
  name: string;
  type?: Exclude<LabeledTextFieldProps["type"], "file">;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disableFormikBlur?: boolean;
};

export function FormikLabeledTextField({
  formik,
  name,
  type = "text",
  onBlur,
  onChange,
  disableFormikBlur = false,
  ...rest
}: FormikLabeledTextFieldProps) {
  const { error, helperText } = getFieldErrorProps(formik, name);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (disableFormikBlur) {
      formik.setFieldTouched(name, true, false);
    } else {
      formik.handleBlur(event);
    }
    onBlur?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    onChange?.(event);
  };

  return (
    <LabeledTextField
      {...rest}
      id={name}
      type={type}
      value={formik.values[name]}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      helperText={helperText}
    />
  );
}

export type FormikLabeledFileFieldProps = Omit<CommonProps, "accept"> & {
  formik: FormikProps<any>;
  name: string;
  accept?: string;
};
